import { PortfolioInfo } from 'communication/types/ContactSupportRequest';
import React, { useEffect, useState } from 'react';
import { Card, Icon } from '@instinet-analytics/semantic-ui-components';
import { ActiveInputType } from 'portfolio/PortfolioState';
import { WidgetInfo } from 'widgets/WidgetInfo';
import {
    extractLocationDetails,
    isEmbeddedDashboard,
    isEmbeddedSingleWidget,
    isNewportLinkedPalette,
    isNewportSingleWidget,
} from 'app/App';
import { VisualisationContextProps } from 'widgets/supportVisualisation/Content/VisualisationContentProps';
import './Overview.scss';
import LabelValue from 'utils/components/LabelValue/LabelValue';
import WidgetsPanel from 'widgets/supportVisualisation/Content/Widgets/WidgetsPanel';
import WidgetPanel from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import { toDateString } from 'widgets/supportVisualisation/SupportVisualisationHelpers';
import { SelectionPanel } from 'widgets/supportVisualisation/Content/SelectionPanel';
import { useServices } from 'app/hooks';
import {
    selectExternalMarketDataSource,
    selectIndigoMarketDataEnabled,
} from 'marketData/store/selectors';
import { initialState } from 'store/State';
import { PortfolioPanel } from 'widgets/supportVisualisation/Content/PortfolioPanel';
import { selectNewportVersionDetails } from 'newport/store/newportAppSlice';

export interface OverviewProps extends VisualisationContextProps {
    source: string;
    viewTick: number;
}

export enum DrilldownPanel {
    None,
    SelectionPanel,
    Portfolio,
}
export default function Overview(props: OverviewProps) {
    const { state, source, width, height, viewTick } = props;

    const { portfolioService } = useServices();

    const indigoMarketDataEnabled = state?.state?.marketData
        ? selectIndigoMarketDataEnabled(state?.state ?? initialState)
        : true;
    const externalMarketDataSource = state?.state?.marketData
        ? selectExternalMarketDataSource(state?.state ?? initialState)
        : undefined;

    const [selectedWidget, setSelectedWidget] = useState<
        WidgetInfo | undefined
    >(undefined);

    const [throwError, setThrowError] = useState(false);

    if (throwError) {
        throw new Error('You asked for it!');
    }

    const [selectedDrilldown, setSelectedDrilldown] = useState<
        DrilldownPanel | undefined
    >(DrilldownPanel.None);

    const onWidgetDrilldown = (widget?: WidgetInfo) => {
        setSelectedDrilldown(DrilldownPanel.None);
        setSelectedWidget(widget);
    };

    const onPanelDrilldown = (panel: DrilldownPanel) => {
        setSelectedDrilldown(panel);
        setSelectedWidget(undefined);
    };

    useEffect(() => {
        setSelectedDrilldown(DrilldownPanel.None);
        setSelectedWidget(undefined);
    }, [viewTick]);

    if (selectedWidget || selectedDrilldown) {
        const drilldownHeaderHeight = 25;
        const drilldownPanelHeight = height - drilldownHeaderHeight;
        return (
            <div className="Overview drilldown">
                {selectedWidget && (
                    <>
                        <div className="drilldown-header">
                            <div className="title">Widget Details</div>
                            <Icon
                                name="close"
                                onClick={() => setSelectedWidget(undefined)}
                            />
                        </div>
                        <WidgetPanel
                            width={width}
                            height={drilldownPanelHeight}
                            widget={selectedWidget}
                            state={state!}
                        />
                    </>
                )}
                {selectedDrilldown === DrilldownPanel.SelectionPanel && (
                    <>
                        <div className="drilldown-header">
                            <div className="title">
                                Selection (first 100 of each selection type
                                shown)
                            </div>
                            <Icon
                                name="close"
                                onClick={() =>
                                    setSelectedDrilldown(DrilldownPanel.None)
                                }
                            />
                        </div>
                        <SelectionPanel
                            portfolio={state!.portfolio!}
                            height={height - 35}
                            width={width}
                            state={state!.state}
                        />
                    </>
                )}
                {selectedDrilldown === DrilldownPanel.Portfolio && (
                    <>
                        <div className="drilldown-header">
                            <div className="title">Portfolio</div>
                            <Icon
                                name="close"
                                onClick={() =>
                                    setSelectedDrilldown(DrilldownPanel.None)
                                }
                            />
                        </div>
                        <PortfolioPanel
                            portfolio={state!.portfolio!}
                            height={height - 35}
                            width={width}
                            state={state!.state}
                        />
                    </>
                )}
            </div>
        );
    }

    const title =
        source +
        (state?.timeStamp
            ? ` - timestamp ${toDateString(state!.timeStamp!)}`
            : '') +
        (state?.name ? ` - from ${state.name}` : '');

    const style = { height: `${height}px` };
    const containerStyle = { width: `${width - 20}px` };

    const orderIdsSource =
        state?.state.portfolio.activeInputType === 'Embedded App'
            ? state?.state.embeddedApp.orderIdsSource
            : undefined;

    const activeInputUsesOrders = state?.state
        ? portfolioService
              .selectPortfolioActiveInput(state.state)
              ?.usesOrdersStream(state.state)
            ? 'Yes'
            : 'No'
        : 'n/a';

    return (
        <div className="Overview" style={style}>
            <div className="overflow-container" style={containerStyle}>
                <div className="title">{title}</div>
                <Card.Group>
                    <Card>
                        <Card.Header>Active Input</Card.Header>
                        <Card.Description>
                            <LabelValue
                                value={activeInputDesc(state?.activeInput)}
                            />
                        </Card.Description>
                        <Card.Description>
                            <LabelValue
                                label={'Uses Orders?'}
                                value={activeInputUsesOrders}
                            />
                        </Card.Description>
                    </Card>
                    <Card>
                        <Card.Header>Application Mode</Card.Header>
                        <Card.Description>
                            <LabelValue
                                value={applicationMode(state?.location)}
                            />
                        </Card.Description>
                    </Card>
                    <Card
                        className={'clickable active'}
                        onClick={() =>
                            onPanelDrilldown(DrilldownPanel.Portfolio)
                        }
                    >
                        <Card.Header>Portfolio</Card.Header>
                        <Card.Description>
                            <LabelValue
                                label={'Orders Count'}
                                value={ordersCount(state?.portfolio)}
                            />
                        </Card.Description>
                        <Card.Description>
                            <LabelValue
                                label={'Filtered Portfolio Count'}
                                value={filteredPortfolioCount(state?.portfolio)}
                            />
                        </Card.Description>
                        {orderIdsSource && (
                            <Card.Description>
                                <LabelValue
                                    label={'Order Ids Source'}
                                    value={orderIdsSource}
                                />
                            </Card.Description>
                        )}
                    </Card>
                    <Card
                        className={'clickable active'}
                        onClick={() =>
                            onPanelDrilldown(DrilldownPanel.SelectionPanel)
                        }
                    >
                        <Card.Header>Selection</Card.Header>
                        {state?.portfolio?.selection && (
                            <>
                                <Card.Description>
                                    <LabelValue
                                        label={'Orders'}
                                        value={
                                            state.portfolio.selection
                                                .selectedOrdersCount
                                        }
                                    />
                                </Card.Description>
                                <Card.Description>
                                    <LabelValue
                                        label={'Positions'}
                                        value={
                                            state.portfolio.selection
                                                .selectedPositionsCount
                                        }
                                    />
                                </Card.Description>
                                <Card.Description>
                                    <LabelValue
                                        label={'Symbol Quantities'}
                                        value={
                                            state.portfolio.selection
                                                .selectedSymbolQuantitiesCount
                                        }
                                    />
                                </Card.Description>
                                <Card.Description>
                                    <LabelValue
                                        label={'Symbols'}
                                        value={
                                            state.portfolio.selection
                                                .selectedSymbolsCount
                                        }
                                    />
                                </Card.Description>
                                <Card.Description>
                                    <LabelValue
                                        label={'Symbology'}
                                        value={
                                            state.portfolio.selection.symbology
                                        }
                                    />
                                </Card.Description>
                            </>
                        )}
                    </Card>
                    <Card>
                        <Card.Header>Market Data</Card.Header>
                        <Card.Description>
                            <LabelValue
                                label={'Indigo Market Data Enabled?'}
                                value={
                                    indigoMarketDataEnabled ? 'true' : 'false'
                                }
                            />
                        </Card.Description>
                        {externalMarketDataSource && (
                            <Card.Description>
                                <LabelValue
                                    label={'External Market Data Source'}
                                    value={externalMarketDataSource}
                                />
                            </Card.Description>
                        )}
                        {orderIdsSource && (
                            <Card.Description>
                                <LabelValue
                                    label={'Order Ids Source'}
                                    value={orderIdsSource}
                                />
                            </Card.Description>
                        )}
                    </Card>
                    <Card>
                        <Card.Header>Versions</Card.Header>
                        <Card.Description>
                            <LabelValue
                                label={'Build Version'}
                                value={
                                    state?.indigoVersion?.buildVersion ?? '-'
                                }
                            />
                            <LabelValue
                                label={'Server Build Version'}
                                value={
                                    state?.indigoVersion?.serverBuildVersion ??
                                    '-'
                                }
                            />

                            <LabelValue
                                label={'Newport Version'}
                                value={
                                    state?.state
                                        ? selectNewportVersionDetails(
                                              state?.state
                                          )?.clientNewportVersion ?? '-'
                                        : '-'
                                }
                            />
                        </Card.Description>
                    </Card>
                </Card.Group>

                <div className="title">Widgets</div>
                <WidgetsPanel
                    {...props}
                    selectedWidget={selectedWidget}
                    onDrilldown={onWidgetDrilldown}
                />
                <div
                    className="title throw-error "
                    onClick={() => {
                        setThrowError(true);
                    }}
                >
                    Throw Error
                </div>
            </div>
        </div>
    );
}

function activeInputDesc(state?: {
    activeInputType: ActiveInputType;
    activeInputWidget?: WidgetInfo;
}) {
    if (!state?.activeInputType) {
        return 'Not found in state';
    }

    const widgetText = state.activeInputWidget
        ? ` - ${state.activeInputWidget.type} - ${state.activeInputWidget.id}`
        : '';

    return `${state.activeInputType}${widgetText}`;
}

function applicationMode(location?: Location) {
    if (!location) {
        return 'Not found in state';
    }
    const { fileName } = extractLocationDetails(location);

    return isNewportSingleWidget(fileName)
        ? 'Newport Single Widget'
        : isNewportLinkedPalette(fileName)
        ? 'Newport Linked Palette'
        : isEmbeddedDashboard(fileName)
        ? 'Embedded Dashboard'
        : isEmbeddedSingleWidget(fileName)
        ? 'Embedded Widget'
        : 'Standalone';
}

function ordersCount(pi?: PortfolioInfo) {
    return pi?.ordersCount ?? 'Not found in state';
}

function filteredPortfolioCount(pi?: PortfolioInfo) {
    return pi?.filteredPortfolioInfoCount ?? 'Not found in state';
}
